import { create } from 'zustand';

/**
 * Global background-job registry (Tier 3 of the progress vocabulary).
 *
 * Holds metadata ONLY — never files, blobs, or item payloads. Any long job
 * that can outlive the surface that launched it registers here so the thin
 * `GlobalActivityBar` can keep the user informed after a sheet closes.
 */
export interface ActivityJob {
	id: string;
	/** Short label, e.g. "Listing on eBay" or "Uploading photos". */
	label: string;
	done: number;
	total: number;
	failed?: number;
	/** Optional callback to reopen the owning surface when the bar is tapped. */
	onOpen?: () => void;
}

interface ActivityStore {
	jobs: Record<string, ActivityJob>;
	startJob: (job: ActivityJob) => void;
	updateJob: (id: string, patch: Partial<Omit<ActivityJob, 'id'>>) => void;
	endJob: (id: string) => void;
}

export const useActivityStore = create<ActivityStore>((set) => ({
	jobs: {},
	startJob: (job) => set((s) => ({ jobs: { ...s.jobs, [job.id]: job } })),
	updateJob: (id, patch) =>
		set((s) => (s.jobs[id] ? { jobs: { ...s.jobs, [id]: { ...s.jobs[id], ...patch } } } : s)),
	endJob: (id) =>
		set((s) => {
			if (!s.jobs[id]) return s;
			const next = { ...s.jobs };
			delete next[id];
			return { jobs: next };
		}),
}));

/** Non-reactive helpers so plain async code can drive a job without hooks. */
export const activity = {
	start: (job: ActivityJob) => useActivityStore.getState().startJob(job),
	update: (id: string, patch: Partial<Omit<ActivityJob, 'id'>>) =>
		useActivityStore.getState().updateJob(id, patch),
	end: (id: string) => useActivityStore.getState().endJob(id),
};
